import { Auth0ProviderOptions } from '@auth0/auth0-react';
import { env, validateConfig } from '../config/env';

// Validate configuration on import
if (!validateConfig()) {
    throw new Error('Invalid Auth0 configuration - missing required environment variables');
}

// Development mode configuration
const isDevelopment = env.app.environment === 'development';

// Auth0 provider configuration with enhanced error handling
export const auth0Config: Auth0ProviderOptions = {
    domain: env.auth0.domain,
    clientId: env.auth0.clientId,
    authorizationParams: {
        redirect_uri: env.auth0.redirectUri,
        audience: env.auth0.audience,
        scope: "openid profile email offline_access read:ping_feedback write:ping_feedback delete:ping_feedback read:ping_projects write:ping_projects delete:ping_projects read:ping_organisations write:ping_organisations delete:ping_organisations admin:ping_system read:ping_system_monitoring write:ping_cache read:ping_analytics write:ping_analytics",
    },
    useRefreshTokens: true,
    cacheLocation: "localstorage",
    
    // Enhanced error handling configuration
    skipRedirectCallback: false, // Always handle redirects properly

    // Development mode configuration
    ...(isDevelopment && {
        // Development-specific settings
        maxAge: 60 * 60 * 24, // 24 hours for development
    }),

    // Production mode configuration
    ...(!isDevelopment && {
        // Production-specific settings for better security
        maxAge: 60 * 60 * 8, // 8 hours for production
    })
};

// Ping API audience configuration
export const getPingApiAudience = (): string => {
    return env.auth0.audience;
};

// Auth0 error handling configuration
export const auth0ErrorConfig = {
    // Map of error codes to user-friendly messages
    errorMessages: {
        'access_denied': 'Access to the Ping Pong Feedback interface was denied. Please check your permissions.',
        'unauthorized': 'You are not authorized to access the Ping Pong Feedback interface.',
        'consent_required': 'Additional consent is required to access this application.',
        'login_required': 'Please log in to access the Ping Pong Feedback interface.',
        'mfa_required': 'Multi-factor authentication is required.',
        'timeout': 'The authentication request timed out. Please try again.',
        'network_error': 'Unable to connect to the authentication service. Please check your internet connection.',
        'invalid_credentials': 'Invalid email or password. Please check your credentials and try again.',
        'user_not_found': 'No account found with that email address. Please check your email or contact support.',
        'account_blocked': 'Your account has been temporarily blocked due to too many failed login attempts. Please try again later or contact support.',
        'admin_unauthorized': 'Your account is not authorized to access the Ping Pong Feedback interface. Please contact your administrator.',
        'interaction_required': 'User interaction is required to complete authentication. Please try logging in again.',
    },
    
    // Default retry configuration
    retryConfig: {
        maxRetries: 3,
        retryDelay: 1000, // 1 second
        backoffMultiplier: 2
    }
};

// Utility function to check if the current environment supports Auth0
export const supportsAuth0 = (): boolean => {
    return !!env.auth0.domain && !!env.auth0.clientId;
};

// Utility function to get organization-specific login URL (for future use)
export const getOrganizationLoginUrl = (returnTo?: string): string => {
    const baseUrl = `https://${env.auth0.domain}/authorize`;
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: env.auth0.clientId,
        redirect_uri: env.auth0.redirectUri,
        scope: 'openid profile email offline_access read:ping_feedback write:ping_feedback delete:ping_feedback read:ping_projects write:ping_projects delete:ping_projects read:ping_organisations write:ping_organisations delete:ping_organisations admin:ping_system read:ping_system_monitoring write:ping_cache read:ping_analytics write:ping_analytics',
        audience: env.auth0.audience,
        ...(returnTo && { state: btoa(JSON.stringify({ returnTo })) })
    });
    
    return `${baseUrl}?${params.toString()}`;
}; 